import React, { useState } from "react";
import { useDispatch } from "react-redux";
import { useHistory } from "react-router-dom";
import { GET_PROVIDER_TIMESHEET, INSERT_TIMESHEET_APPROVAL } from "src/api-setup/api-endpoints";
import { POSTAPI } from "src/api-setup/api-helper";
import { USER_GRID_DEFAULT_PAGE_SIZE } from "src/constants/grid";
import { getTenantCookieData, getUserCredential } from "src/lib/cookies";
import { setNewMessage } from "src/redux/actions/setting-actions";
import { GridColumnDataMapper } from "src/utils/utils";

const initialDataState = {
  skip: 0,
  take: USER_GRID_DEFAULT_PAGE_SIZE,
};
let defaultItem = {
  text: USER_GRID_DEFAULT_PAGE_SIZE,
  id: 2,
};

const useApproverDashboard = () => {
  const history = useHistory();
  const cookiesData = getUserCredential();

  const header = {
    "x-access-token": cookiesData.secretToken,
  };

  const dispatch = useDispatch();
  const defaultPageSize = getTenantCookieData("record_per_page");

  const [data, setData] = React.useState([]);
  const [columns, setColumns] = React.useState([]);
  const [dataCount, setDataCount] = React.useState(0);
  const [sortStr, setSortStr] = React.useState("");

  const [initialLoading, setLoadingState] = React.useState(false);
  const [isRefreshLoading, setIsRefreshLoading] = useState(false);
  const [isAPICalling, setAPICallingState] = useState(false);

  const [originalData, setOriginalData] = React.useState([]);

  const columnXml = `{"resultset":{"metadata":[{"field":[{"$":{"name":"temp.timesheet_mstr_key","label":"TIMESHEET ID","type":"number","filterable":"1"},"format":[""]},{"$":{"name":"temp.status","label":"STATUS","type":"string","filterable":"1"},"format":[""]},{"$":{"name":"temp.employee_id","label":"EMPLOYEE ID","type":"number","filterable":"1"},"format":[""]},{"$":{"name":"temp.provider_name","label":"PROVIDER NAME","type":"string","filterable":"1"},"format":[""]},{"$":{"name":"temp.specialty_name","label":"SPECIALTY NAME","type":"string","filterable":"1"},"format":[""]},{"$":{"name":"temp.location_name","label":"LOCATION","type":"string","filterable":"1"},"format":[""]},{"$":{"name":"temp.contract_type","label":"PAY REQUEST TYPE","type":"string","filterable":"1"},"format":[""]},{"$":{"name":"temp.contract_name","label":"PAY REQUEST NAME","type":"string","filterable":"1"},"format":[""]},{"$":{"name":"temp.shift_type_name","label":"SHIFT TYPE","type":"string","filterable":"1"},"format":[""]},{"$":{"name":"temp.ts_date_local","label":"SERVICE DATE","type":"date","filterable":"1"},"format":["{0:MM/DD/YYYY}"]},{"$":{"name":"temp.post_date_local","label":"POST DATE","type":"date","filterable":"1"},"format":["{0:MM/dd/yyyy}"]},{"$":{"name":"temp.total_quantity","label":"TOTAL QUANTITY","type":"string","filterable":"0"},"format":[""]},{"$":{"name":"temp.budget","label":"AMOUNT","type":"number","filterable":"0"},"format":[""]},{"$":{"name":"temp.period_type","label":"PERIOD TYPE","type":"string","filterable":"1"},"format":[""]}]}]}}`;
  const fetchTimesheetsDataAsync = (payload) => {
    setAPICallingState(true);
    return new Promise(function (resolve, reject) {
      POSTAPI(false, GET_PROVIDER_TIMESHEET, null, header, payload, dispatch)
        .then((res) => {
          if (res) {
            if (res.data.out_parameters.out_ret_code > 0) {
              setIsRefreshLoading(true);
            }
            setIsRefreshLoading(true);
            const flattenData = res.data.row_result;
            if (payload.in_load_type === "grid-reload") {
              const columData = GridColumnDataMapper(columnXml)?.map((obj) => {
                return { ...obj, width: 150 };
              });
              columData.unshift({
                title: "ACTION",
                field: "is_selected",
                type: "boolean",
                show: true,
                width: 130,
                filterable: "1",
                filter: "boolean",
              });
              setColumns(columData);
            }
            
            setSortStr(res.data.out_parameters?.out_column_labels ?? "");

            const finalData = flattenData.map((item, idx) => ({ ...item, is_selected: 0, rowId: idx }));

            setData(finalData);
            setOriginalData(finalData);
            setDataCount(finalData.length);
            setIsRefreshLoading(false);
            resolve(finalData);
          }
        })
        .then((res) => {
          return res;
        })
        .catch((err) => {
          console.log("ERRe", err);
        })
        .finally(() => {
          setAPICallingState(false);
        });
    });
  };

  const onSubmitClick = async (status) => {
    if (!cookiesData?.out_user_id) return;
    setAPICallingState(true);
    try {
      const apiHeaders = { "x-access-token": cookiesData.secretToken };
      const requestBody = {
        in_user_id: cookiesData.out_user_id,
        in_tenant_id: cookiesData.out_tenant_id,
        in_timesheet_mstr_key: data
          .filter((it) => it.is_selected === 1)
          .map((it) => ({
            id: it.timesheet_mstr_key,
          })),
        in_approver_mstr_key: cookiesData.out_user_id.toString(),
        in_status: status,
      };

      const response = await POSTAPI(false, INSERT_TIMESHEET_APPROVAL, null, apiHeaders, requestBody, dispatch);

      // Any approvals that landed on Hold drive the hold popup instead of the toast. This runs
      // regardless of out_ret_code so a partial failure (some held, one failed) still shows it.
      const heldKeys = response?.data?.out_parameters?.heldTimesheetMstrKey || [];
      if (status === "4" && heldKeys.length > 0) {
        return heldKeys; // finally clears the API-calling state
      }
      if (response?.data?.out_parameters?.out_ret_code === 0) {
        const message = status === "4" ? "Timesheet approved successfully" : "Timesheet rejected successfully";
        dispatch(setNewMessage({ message: message, type: "Success", show: true }));
      }
    } catch (error) {
      console.log(error, "error");
    } finally {
      setAPICallingState(false);
    }
    return [];
  };

  return [
    {
      data,
      columns,
      dataCount,
      initialLoading,
      isRefreshLoading,
      isAPICalling,
      originalData,
      setData,
      setAPICallingState,
      sortStr,
    },
    { fetchTimesheetsDataAsync, onSubmitClick },
  ];
};

export default useApproverDashboard;
